'use client';

import { useEffect, useRef } from 'react';
import { logError } from '@zg-rentals/log-error';
import { getModelContext, type WebMcpTool } from './modelContext';

/**
 * Registers a fixed set of WebMCP tools for as long as the calling component is mounted.
 *
 * Registration happens once per tool name, but `execute` is dispatched through a ref, so a
 * tool registered on mount still closes over the newest render's state. Without that the
 * agent would mutate whatever filters were active when the shell first mounted.
 *
 * No-ops when the browser has no `document.modelContext`, and when whatever is behind it fails,
 * so callers never need to feature-detect or defend against a broken implementation.
 */
export function useWebMcpTools(tools: Array<WebMcpTool>): void {
  const toolsRef = useRef(tools);
  toolsRef.current = tools;

  const toolNames = tools.map((tool) => tool.name).join(',');

  useEffect(() => {
    const modelContext = getModelContext();
    if (!modelContext) return undefined;

    const controller = new AbortController();

    for (const registered of toolsRef.current) {
      const { name } = registered;

      // `document.modelContext` is not necessarily the browser's own implementation. Any
      // extension can define it, and one shipping shim postMessages the whole descriptor,
      // which cannot structured-clone `execute` and throws DataCloneError (HP-9103). That
      // throw is synchronous, so it has to be caught here rather than handled as a
      // rejection: an error escaping this effect reaches the root layout boundary and
      // replaces the page. Catching per tool also stops one bad tool aborting the rest.
      try {
        void Promise.resolve(
          modelContext.registerTool(
            {
              ...registered,
              execute: (args, context) => {
                const current = toolsRef.current.find((tool) => tool.name === name) ?? registered;
                return current.execute(args, context);
              },
            },
            { signal: controller.signal },
          ),
        ).catch(() => {
          // A rejected registration (duplicate name, revoked trial token) is an expected
          // condition, so it stays silent. The tool is simply absent, which is the same
          // state as a browser without WebMCP.
        });
      } catch (error) {
        // A synchronous throw is not expected: it means the implementation behind
        // `document.modelContext` is non-conforming. Report it under its own errorType so it
        // can be told apart from our own defects and filtered out of error dashboards.
        logError({
          error: error instanceof Error ? error : new Error(String(error)),
          errorType: 'WebMcpRegisterToolFailed',
        });
      }
    }

    return () => controller.abort();
  }, [toolNames]);
}
